import React, { useState, useEffect } from 'react';
import { MapPinIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { getDistance } from 'geolib';
import { useSelector } from 'react-redux';
import { selectUserLocation } from '../../../../../redux/slices/locationSlice';

/**
 * PickupOption component for showing local pickup in DeliveryMethodSelection
 * Only shows distance to pickup point for privacy - actual address revealed after purchase
 */
const PickupOption = ({
  deliveryOption,
  setDeliveryOption,
  pickUpAddress,
  selectedAddress,
  calculateDistance,
  userLocation,
  sellerAddress
}) => {
  // Get user location from Redux for non-authenticated users
  const reduxUserLocation = useSelector(selectUserLocation);
  const [distanceInfo, setDistanceInfo] = useState(null);
  const [isCalculatingDistance, setIsCalculatingDistance] = useState(false);
  
  // Calculate distance using geolib
  useEffect(() => {
    // Skip calculation if we don't have pickup address
    if (!pickUpAddress || !pickUpAddress.latitude || !pickUpAddress.longitude) {
      console.log('Missing pickup location coordinates');
      setDistanceInfo(null);
      return;
    }
    
    // Determine which location to use for calculation
    let userCoords = null;
    
    // For authenticated users with address
    if (selectedAddress?.latitude && selectedAddress?.longitude) {
      console.log('Using selected address coordinates for pickup distance');
      userCoords = { 
        latitude: selectedAddress.latitude, 
        longitude: selectedAddress.longitude 
      };
    } 
    // For non-authenticated users with browser location
    else if (reduxUserLocation?.coordinates) {
      console.log('Using browser geolocation for pickup distance');
      userCoords = reduxUserLocation.coordinates;
    }
    
    // If we have coordinates, calculate the distance
    if (userCoords) {
      setIsCalculatingDistance(true);
      
      try {
        const sellerCoords = { 
          latitude: pickUpAddress.latitude, 
          longitude: pickUpAddress.longitude 
        };
        
        const distanceInMeters = getDistance(userCoords, sellerCoords);
        
        // Convert to appropriate units based on user's region
        const isUSAddress = 
          selectedAddress?.country === 'US' || 
          selectedAddress?.countryCode === 'US' ||
          reduxUserLocation?.country === 'US';
        
        let distanceText;
        if (isUSAddress) {
          const distanceInMiles = (distanceInMeters / 1609.34).toFixed(1);
          distanceText = `${distanceInMiles} miles away`;
        } else {
          const distanceInKm = (distanceInMeters / 1000).toFixed(1);
          distanceText = `${distanceInKm} km away`;
        }
        
        setDistanceInfo(distanceText);
        console.log(`Pickup distance calculated: ${distanceText}`);
      } catch (error) {
        console.error('Error calculating pickup distance:', error);
        setDistanceInfo(null);
      } finally {
        setIsCalculatingDistance(false);
      }
    } else {
      setDistanceInfo(null);
    }
  }, [pickUpAddress, selectedAddress, reduxUserLocation?.coordinates]);
  
  // Show city and postal code for pickup location
  const getGeneralLocation = () => {
    if (!pickUpAddress) return 'Location available after purchase';
    
    // Get city and postal code
    const city = pickUpAddress.city || '';
    const postalCode = pickUpAddress.postalCode || pickUpAddress.zipCode || '';
    
    // Capitalize city
    const formattedCity = city.split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
    
    // Build the display string with both city and postal code
    if (formattedCity && postalCode) {
      return `Pickup in ${formattedCity}, ${postalCode}`;
    } else if (formattedCity) {
      return `Pickup in ${formattedCity}`;
    } else if (postalCode) {
      return `Pickup near ${postalCode}`;
    }
    
    return 'Pickup location available after purchase';
  };
  
  // Handle option selection with toggle functionality
  const handleOptionSelect = () => {
    // If already selected, toggle off, otherwise toggle on
    if (deliveryOption === 'pickup') {
      setDeliveryOption(null);
    } else {
      setDeliveryOption('pickup');
    }
  };
  
  return (
    <div 
      className={`flex items-center p-3 rounded-md cursor-pointer ${
        deliveryOption === 'pickup' ? 'bg-brand-teal bg-opacity-10 border border-brand-teal' : 'border border-gray-200'
      }`}
      onClick={handleOptionSelect}
    >
      <input
        type="radio"
        checked={deliveryOption === 'pickup'}
        onChange={handleOptionSelect}
        className="h-4 w-4 text-brand-teal focus:ring-brand-teal"
      />
      <MapPinIcon className="h-5 w-5 ml-2 text-gray-500" />
      <div className="ml-2 flex-1">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-gray-900">Local Pickup</span>
          
          {/* Always show distance even when not selected */}
          {isCalculatingDistance ? (
            <div className="flex items-center text-xs text-gray-500">
              <ArrowPathIcon className="h-3 w-3 animate-spin mr-1" />
              <span>Calculating...</span>
            </div>
          ) : distanceInfo ? (
            <span className="text-sm font-medium">
              {distanceInfo}
            </span>
          ) : (
            selectedAddress && (
              <span className="text-xs text-gray-500">
                Distance unavailable
              </span>
            )
          )}
        </div>
        
        <div className="flex flex-col text-xs text-gray-500">
          <span>{getGeneralLocation()}</span>
          <span className="text-xs text-gray-400 italic">Exact address available after purchase</span>
        </div>
      </div>
    </div>
  );
};

export default PickupOption; 